import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, FileText, GraduationCap, Sparkles, Unlink } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

// Short pretty-printer for created_at timestamps in the bell dropdown
function ago(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "Just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
  return fmtDate(iso);
}

const KIND_META = {
  client_upload: {
    icon: FileText,
    color: "var(--ll-ink)",
  },
  statement_parsed: {
    icon: FileText,
    color: "#065F46",
  },
  chain_break: {
    icon: Unlink,
    color: "#DC2626",
  },
  training_reminder: {
    icon: GraduationCap,
    color: "#5B21B6",
  },
  default: {
    icon: Sparkles,
    color: "var(--ll-ink-muted)",
  },
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get("/notifications", { params: { limit: 20 } });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch {
      // silent — bell is best-effort
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    // Reload on tenant switch so the count reflects the new workspace
    const onTenantChange = () => load();
    window.addEventListener("tenantChanged", onTenantChange);
    return () => {
      clearInterval(id);
      window.removeEventListener("tenantChanged", onTenantChange);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const openRow = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    setOpen(false);
    await load();
    if (n.url) navigate(n.url);
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      await load();
    } catch { /* ignore */ }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        aria-label="Notifications"
        className="relative w-9 h-9 grid place-items-center border hover:bg-[var(--ll-mint)] transition-colors"
        style={{ borderColor: "var(--ll-border)" }}
      >
        <Bell size={15} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--ll-ochre)] text-white text-[10px] font-bold grid place-items-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-1rem)] bg-white border shadow-lg z-50"
          style={{ borderColor: "var(--ll-border)" }}
        >
          <div
            className="px-4 py-2.5 border-b flex items-center justify-between"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <div className="font-display font-bold text-sm">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="text-[11px] flex items-center gap-1 text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
              >
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto" data-testid="notification-list">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="notification-empty">
                You&apos;re all caught up.
              </div>
            ) : (
              items.map((n) => {
                const meta = KIND_META[n.kind] || KIND_META.default;
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => openRow(n)}
                    data-testid={`notification-row-${n.id}`}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b hover:bg-[var(--ll-bg-sidebar)] transition-colors ${n.read ? "opacity-70" : ""}`}
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <div
                      className="w-7 h-7 grid place-items-center shrink-0"
                      style={{ background: "var(--ll-mint)", color: meta.color }}
                    >
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {n.title}
                        {!n.read && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[var(--ll-ochre)]" />}
                      </div>
                      <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5 line-clamp-2">{n.body}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-1">
                        {ago(n.created_at)}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
